/* CUSTOM VARIANT GALLERY */
(() => {
  'use strict';

  const SELECTORS = {
    root: '[data-custom-variant-gallery]',
    productJson: '[data-product-json]',
    variantIdInput: 'input[name="id"]',
    productPage: '.product__page',
    formWrapper: '[data-form-wrapper]',
    cartBar: '#cart-bar',
    group: '[data-custom-gallery-group]',
    fallbackGroup: '[data-custom-gallery-group="fallback"]',
    slide: '[data-custom-gallery-slide]',
    thumb: '[data-custom-gallery-thumb]',
    template: '[data-custom-gallery-pswp-template]',
    activePswpTemplate: '[data-pswp-thumbs-template]',
    headerCandidates:
      '[data-header-sticky], [data-header-height], ticker-bar, [data-section-type*="announcement"]',
  };

  const CLASSES = {
    active: 'media--active',
    hidden: 'media--hidden',
    hiding: 'media--hiding',
    thumbActive: 'is-active',
    cartBarVisible: 'is-custom-variant-gallery-visible',
    sticky: 'is-sticky',
  };

  const DESKTOP_BREAKPOINT = 750;
  const STICKY_GAP = 12;

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  class CustomVariantGallery {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-section-type="product"]');
      this.product = this.getProduct();
      this.groups = Array.from(root.querySelectorAll(SELECTORS.group));
      this.activePswpTemplate = root.querySelector(
        SELECTORS.activePswpTemplate
      );
      this.productPage = this.section?.querySelector(SELECTORS.productPage);
      this.cartBar = this.section?.querySelector(SELECTORS.cartBar);
      this.formWrapper = this.getMainFormWrapper();
      this.scrollFrame = null;

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onThumbnailClick = this.onThumbnailClick.bind(this);
      this.queueViewportUpdate = this.queueViewportUpdate.bind(this);
    }

    getProduct() {
      const productJson = this.section?.querySelector(SELECTORS.productJson);

      if (!productJson) {
        return null;
      }

      try {
        return JSON.parse(productJson.textContent);
      } catch (error) {
        console.warn(
          'CUSTOM VARIANT GALLERY: unable to parse product JSON.',
          error
        );
        return null;
      }
    }

    getMainFormWrapper() {
      if (!this.section) {
        return null;
      }

      return (
        Array.from(this.section.querySelectorAll(SELECTORS.formWrapper)).find(
          (wrapper) => !wrapper.closest(SELECTORS.cartBar)
        ) || null
      );
    }

    init() {
      if (!this.section || !this.product || !this.groups.length) {
        return;
      }

      this.section.setAttribute(
        'data-custom-variant-gallery-section',
        'true'
      );

      this.section.addEventListener(
        'theme:variant:change',
        this.onVariantChange
      );

      this.root.addEventListener('click', this.onThumbnailClick);

      /*
        CUSTOM VARIANT GALLERY
        Runs after Broadcast's own scroll handler through requestAnimationFrame.
      */
      document.addEventListener('theme:scroll', this.queueViewportUpdate);
        document.addEventListener('theme:resize', this.queueViewportUpdate);

        /* CUSTOM VARIANT GALLERY
        Runs directly from browser scrolling as well as Broadcast's theme event. */
        window.addEventListener('scroll', this.queueViewportUpdate, {
        passive: true,
        });

        window.addEventListener('load', this.queueViewportUpdate, { once: true });

      this.activateVariant(this.getInitialVariant());
      this.queueViewportUpdate();

      this.root.dataset.customGalleryInitialised = 'true';
    }

    getInitialVariant() {
      const variantId = this.section.querySelector(
        SELECTORS.variantIdInput
      )?.value;

      return (
        this.product.variants.find(
          (variant) => String(variant.id) === String(variantId)
        ) || null
      );
    }

    onVariantChange(event) {
      this.activateVariant(event.detail?.variant || null);
    }

    getGroupForVariant(variant) {
      /*
        CUSTOM VARIANT GALLERY
        Variants without assigned main media use the full-gallery fallback.
      */
      if (!variant?.featured_media?.id) {
        return this.root.querySelector(SELECTORS.fallbackGroup);
      }

      return (
        this.root.querySelector(
          `[data-custom-gallery-group][data-custom-gallery-variant-id="${escapeSelector(
            variant.id
          )}"]`
        ) || this.root.querySelector(SELECTORS.fallbackGroup)
      );
    }

    getActiveGroup() {
      return this.groups.find((group) => !group.hidden) || null;
    }

    activateVariant(variant) {
      const group = this.getGroupForVariant(variant);

      if (!group) {
        return;
      }

      this.groups.forEach((item) => {
        const isActiveGroup = item === group;

        item.hidden = !isActiveGroup;
        item.setAttribute('aria-hidden', String(!isActiveGroup));
      });

      const preferredMediaId = variant?.featured_media?.id
        ? String(variant.featured_media.id)
        : null;

      const firstSlide = group.querySelector(SELECTORS.slide);

      const preferredSlide = preferredMediaId
        ? group.querySelector(
            `[data-custom-gallery-media-id="${escapeSelector(
              preferredMediaId
            )}"]`
          )
        : null;

      this.setActiveSlide(group, preferredSlide || firstSlide);
      this.syncPhotoSwipeTemplate(group);
      this.queueViewportUpdate();
    }

    setActiveSlide(group, selectedSlide) {
      if (!group || !selectedSlide) {
        return;
      }

      const selectedMediaId = selectedSlide.dataset.customGalleryMediaId;

      group.querySelectorAll(SELECTORS.slide).forEach((slide) => {
        const isActive = slide === selectedSlide;

        slide.classList.toggle(CLASSES.active, isActive);
        slide.classList.toggle(CLASSES.hidden, !isActive);
        slide.classList.remove(CLASSES.hiding);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      group.querySelectorAll(SELECTORS.thumb).forEach((thumb) => {
        const isActive =
          thumb.dataset.customGalleryMediaId === selectedMediaId;

        const thumbItem = thumb.closest('.product__thumb');

        thumbItem?.classList.toggle(CLASSES.thumbActive, isActive);

        thumb.setAttribute('aria-current', String(isActive));
        thumb.tabIndex = isActive ? 0 : -1;
      });
    }

    syncPhotoSwipeTemplate(group) {
      const source = group.querySelector(SELECTORS.template);

      if (this.activePswpTemplate && source) {
        this.activePswpTemplate.innerHTML = source.innerHTML;
      }
    }

    onThumbnailClick(event) {
      const thumb = event.target.closest(SELECTORS.thumb);

      if (!thumb || !this.root.contains(thumb)) {
        return;
      }

      event.preventDefault();

      const group = thumb.closest(SELECTORS.group);

      const targetSlide = group?.querySelector(
        `[data-custom-gallery-media-id="${escapeSelector(
          thumb.dataset.customGalleryMediaId
        )}"]`
      );

      this.setActiveSlide(group, targetSlide);
      this.queueViewportUpdate();
    }

    queueViewportUpdate() {
      if (this.scrollFrame) {
        return;
      }

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;

        this.updateStickyForm();
        this.updateCartBar();
      });
    }

    getHeaderBottom() {
      const cssHeaderHeight =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            '--header-height'
          )
        ) || 0;

      let visibleHeaderBottom = 0;

      document
        .querySelectorAll(SELECTORS.headerCandidates)
        .forEach((element) => {
          const rect = element.getBoundingClientRect();

          if (
            rect.top <= 2 &&
            rect.bottom > 0 &&
            rect.bottom < window.innerHeight * 0.65
          ) {
            visibleHeaderBottom = Math.max(
              visibleHeaderBottom,
              rect.bottom
            );
          }
        });

      return Math.ceil(
        Math.max(cssHeaderHeight, visibleHeaderBottom) + STICKY_GAP
      );
    }

    updateStickyForm() {
      if (!this.formWrapper) {
        return;
      }

      const stickyEnabled =
        this.section.getAttribute('data-sticky-enabled') === 'true';

      const activeSlides = this.getActiveGroup()?.querySelector(
        '.product__slides'
      );

      const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;

      if (!stickyEnabled || !isDesktop || !activeSlides) {
        this.formWrapper.classList.remove(CLASSES.sticky);
        this.formWrapper.style.removeProperty(
          '--custom-variant-gallery-sticky-top'
        );
        return;
      }

      const shouldStick =
        activeSlides.offsetHeight > this.formWrapper.offsetHeight;

      this.formWrapper.classList.toggle(CLASSES.sticky, shouldStick);

      if (shouldStick) {
        this.formWrapper.style.setProperty(
          '--custom-variant-gallery-sticky-top',
          `${this.getHeaderBottom()}px`
        );
      } else {
        this.formWrapper.style.removeProperty(
          '--custom-variant-gallery-sticky-top'
        );
      }
    }

    updateCartBar() {
        /*
            CUSTOM VARIANT GALLERY
            Broadcast continues to toggle .is-visible using its normal form-based
            threshold. This section uses its own class as the visual source of truth,
            preventing flicker during fast upward scrolling.
        */
        if (
            !this.cartBar ||
            !this.section.hasAttribute('data-cart-bar-enabled') ||
            !this.productPage
        ) {
            return;
        }

        const productPageBottom = this.productPage.getBoundingClientRect().bottom;
        const headerBottom = this.getHeaderBottom();

        const shouldShow = productPageBottom <= headerBottom;

        this.cartBar.classList.toggle(
            CLASSES.cartBarVisible,
            shouldShow
        );
    }
  }

  function initialise(container = document) {
    container.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (root.dataset.customGalleryInitialised === 'true') {
        return;
      }

      new CustomVariantGallery(root).init();
    });
  }

  document.addEventListener('DOMContentLoaded', () => initialise());

  document.addEventListener('shopify:section:load', (event) => {
    initialise(event.target);
  });
})();